import { APP_INITIALIZER, Injectable, Provider } from '@angular/core';
import { AuthService } from '@abp/ng.core';
import { OAuthService } from 'angular-oauth2-oidc';
import { filter } from 'rxjs/operators';
import { BiometricLoginService } from './biometric-login.service';

/**
 * Mantem o cofre biometrico (SecureStorage) em sincronia com a rotacao do
 * refresh token.
 *
 * O problema (task do login biometrico): o backend OpenIddict e rolling, entao
 * cada refresh silencioso gera um refresh token novo no localStorage, mas o
 * cofre so era gravado no login. Num cold-start posterior a biometria lia um RT
 * ja consumido e o `loginUsingGrant('refresh_token')` batia 400
 * ("sua sessao expirou"), quebrando o principal atalho de reentrada do
 * motorista.
 *
 * Aqui assinamos o evento `token_received` do angular-oauth2-oidc (disparado no
 * login E em toda rotacao de refresh) e regravamos o RT novo no cofre. So
 * sincroniza se o cofre ja existir: o estabelecimento continua no login, pra
 * nao passar a gravar credencial de quem nunca usou biometria.
 */
@Injectable({ providedIn: 'root' })
export class BiometricSessionSyncService {
  constructor(
    private readonly oAuthService: OAuthService,
    private readonly authService: AuthService,
    private readonly biometricLogin: BiometricLoginService,
  ) {}

  start(): void {
    this.oAuthService.events
      .pipe(filter(event => event.type === 'token_received'))
      .subscribe(() => this.syncVault());
  }

  private async syncVault(): Promise<void> {
    try {
      if (!(await this.biometricLogin.hasStoredRefreshToken())) {
        return;
      }
      const refreshToken = this.authService.getRefreshToken();
      if (refreshToken) {
        await this.biometricLogin.saveRefreshToken(refreshToken);
      }
    } catch (error) {
      console.error('Falha ao sincronizar o cofre biometrico:', error);
    }
  }
}

export const BIOMETRIC_SESSION_SYNC_PROVIDER: Provider = {
  provide: APP_INITIALIZER,
  multi: true,
  useFactory: (sync: BiometricSessionSyncService) => () => sync.start(),
  deps: [BiometricSessionSyncService],
};
